/**
 * @module ui.cart
 * @version 12.6.0
 * @description LIFTR Cart Web Component with Selective Section Rendering
 *
 * Server renders everything. JS handles:
 * - Click delegation for quantity +/-/remove
 * - Subscription plan selection (Modal handles open/close via ui.modal.js)
 * - Cart note auto-save on blur
 * - Discount code apply/remove
 * - API calls to Shopify via liftrShop
 * - Selective section update (panels stay open)
 * - Pre-DOM merging of split line items (Automatic Discounts)
 * - ARIA announcements
 * - Visual notifications via Notification API
 * - Snapshot animations (progress bar morph, count pill pop)
 * - Cart count sync
 * - Nested Panel Orchestration (menu, note, discount)
 *
 * @element liftr-cart
 *
 * @fires liftr:cart:refreshed - After content replaced
 * @fires liftr:cart:item-removed - After item removal
 * @fires liftr:cart:plan-changed - After subscription plan changed
 * @fires liftr:cart:note-saved - After note saved
 * @fires liftr:cart:discount-applied - After discount code applied
 * @fires liftr:cart:discount-removed - After discount code removed
 * @fires liftr:cart:discount-error - When discount code is invalid
 *
 * @attr {string} liftr-id - Section identifier
 * @attr {string} liftr-state - State output (empty, loading, has-note, has-discounts)
 * @attr {string} liftr-merge - Merged item data (JSON, auto-set)
 *
 * Roles:
 * - cart-main:               Updateable wrapper for main cart panel content
 * - cart-discount:           Updateable wrapper for discount panel content
 * - cart-item-count:         Cart item count in header (for animation)
 * - cart-discount-count:     Discount count in header (for animation)
 * - cart-item:               Line item container (has liftr-id for line key)
 * - cart-item-plus:          Increment quantity button
 * - cart-item-minus:         Decrement quantity button
 * - cart-item-remove:        Remove item button
 * - cart-item-quantity:      Quantity display/input
 * - cart-item-price:         Line price display (updated for merged items)
 * - cart-subscription-toggle: Subscription picker trigger button (for loading state)
 * - cart-item-subscription-option: Dropdown option (liftr-id for plan ID)
 * - cart-note:               Cart note textarea (auto-save on blur)
 * - cart-rewards-bar:        Rewards progress bar (for animation)
 * - cart-live:               ARIA live region for announcements
 * - cart-discount-input:     Discount code text input
 * - cart-discount-apply:     Apply discount button
 * - cart-discount-tag:       Applied discount tag (liftr-id for code)
 * - cart-discount-remove:    Remove discount button (within tag)
 * - cart-discount-error:     Error message container
 * 
 * Panel Orchestration Roles (Nested Pattern):
 * - cart-panel-menu:         Menu dropdown panel (liftr-panel element)
 * - cart-panel-note:         Note panel (liftr-panel element)
 * - cart-panel-discount:     Discount panel (liftr-panel element)
 * - cart-menu-toggle:        Button to open menu panel
 * - cart-note-toggle:        Button to open note panel
 * - cart-discount-toggle:    Button to open discount panel
 *
 * I18n Keys (cart namespace):
 * - reward_unlocked: "{{name}} freigeschaltet" / "{{name}} unlocked"
 * - reward_locked: "Noch {{amount}} bis {{name}}" / "{{amount}} until {{name}}"
 * - stock_limit: "Maximale Menge erreicht" / "Maximum quantity reached"
 *
 * @changelog
 * v12.6.0 - FIX: Product ATC notification coordination
 *         - Cart defers reward flush during Product Full Cycle
 *         - New LISTEN: PRODUCT_ADDING, PRODUCT_ERROR
 *         - No timeout — Product's try/catch guarantees completion
 *         - Fixes: "X freigeschaltet" appearing before "Produkt hinzugefügt"
 * v12.6.0 - NEW: Discount MBW hint notification
 *         - Shows minimum order value when discount code is inapplicable
 *         - Lookup: exact match → longest prefix match (settings.discounts)
 *         - Only shown when cart total below threshold
 *         - Supports expiry date display via discount_hint_expires key
 *         - New i18n keys: discount_hint, discount_hint_expires 
 * v12.5.2 - FIX: Stagger between multiple pending notifications
 *         - Was: all pending fired simultaneously after single delay
 *         - Now: each notification offset by (index + 1) × NOTIFICATION_STAGGER
 *         - New #flushTimeoutIds for proper cleanup on unmount()
 *         - Fixes overlapping reward notifications when 2+ thresholds crossed
 * v12.5.1 - FIX: Use base class getCssDuration() for stagger parsing
 *         - Fixes unit mismatch (0.2s parsed as 0.2ms)
 *         - Replaces inline parseFloat with this.getCssDuration()
 * v12.5.0 - FIX: Deferred reward notifications (queue + flush after refresh)
 *         - New #pendingNotifications queue for reward unlock/lock
 *         - Reward handlers push to queue instead of immediate #notify()
 *         - #performCartOperation(): flush after operation notification (stagger)
 *         - #refresh(): flush for external triggers (!#isUpdating guard)
 *         - Stagger delay from CSS: FX.PROPERTIES.NOTIFICATION_STAGGER
 *         - Sequence: "Menge aktualisiert" → stagger → "X freigeschaltet"
 * v12.4.0 - FIX: Cart-aware quantity validation (post-check)
 *         - #performCartOperation() accepts optional validate callback
 *         - validate() runs after API success, before showing SUCCESS state
 *         - Catches silent Shopify inventory caps (200 OK, quantity unchanged)
 *         - On validation failure: skip SUCCESS, refresh, show INFO notification
 *         - #updateItem() and #updateMergedItem() use #validateQuantity()
 *         - New #validateQuantity(key, requestedQty) helper
 *         - #updateMergedItem() now receives key parameter
 *         - New i18n key: stock_limit
 *         - Removed all i18n fallback strings (consistent with ui.product.js v5.5.0)
 * v12.3.1 - AUDIT COMPLIANCE - Quality Checks v4.8
 *         - Added LISTEN constant (9 external events → named constants)
 *         - Added PROPERTIES constant (empty, for export consistency)
 *         - Updated export to include LISTEN and PROPERTIES
 *         - All hardcoded event strings replaced with LISTEN.*
 * v12.3.0 - FIX: Nested ATC guard prevents DOM replace during product Full Cycle
 *         - #hasActiveNestedATC() checks cart-main for active fx-loading buttons
 *         - liftr:product:added skips refresh when nested product is mid-ATC
 *         - New liftr:product:added listener triggers deferred refresh
 *         - Fixes race condition: recommendations slider products inside cart-main
 *           had their ATC button ripped from DOM mid-animation by Section Rendering
 * v12.2.0 - REFACTOR: Generic fx-loading lifecycle via createState + await* helpers
 *         - All button state management now uses this.createState(button)
 *         - Replaced manual setAttribute/removeAttribute with StateManager API
 *         - Two patterns: Fire & Replace (success → refresh wipes button)
 *           and Full Cycle (error → awaitResultExit, button persists in DOM)
 *         - #performCartOperation(), #handleDiscountApply(), #handleDiscountRemove()
 *         - Simplified SLIDE exit block in #refresh() (safety net only)
 * v12.1.0 - REFACTOR: Use base class fx-loading helpers
 *         - Removed private #awaitLoading(), #getResultDuration(), #awaitResult()
 *         - Now uses this.awaitLoading(), this.getResultDuration(), this.awaitResult()
 *         - Single Source of Truth in core.module.js v4.6.0
 *         - Removed console.log debug statements from #handlePlanSelect()
 * v12.0.0 - BREAKING: Nested Pattern for internal panels
 *         - Cart orchestrates menu/note/discount panels via Public API
 *         - Removed liftr-id/liftr-target for internal panels
 *         - New ROLES: PANEL_MENU, PANEL_NOTE, PANEL_DISCOUNT
 *         - New ROLES: MENU_TOGGLE, NOTE_TOGGLE, DISCOUNT_TOGGLE
 *         - #bindPanelOrchestration() for toggle → panel.open() binding
 *         - Updated #bindPanelEvents() for deferred plan selection
 * v11.8.0 - REFACTOR: Import TYPES, DURATIONS from api.notification.js
 *         - Single Source of Truth for notification constants
 *         - Removed local NOTIFICATION_TYPES, NOTIFICATION_DURATION_* constants
 * v11.7.2 - FIX: Reward notifications now use i18n (reward_unlocked, reward_locked)
 *         - CHANGE: reward-locked uses INFO instead of WARNING (motivating, not warning)
 * v11.7.1 - NEW: Reward notifications for unlock/lock events
 *         - Shows "{name} freigeschaltet" on reward unlock
 *         - Shows "Noch {amount} bis {name}" on reward lock
 * v11.7.0 - NEW: Visual notifications via Notification API
 * v11.6.0 - REFACTOR: Generic #performCartOperation() helper
 * v11.5.0 - NEW: Loading/Success states for subscription toggle button
 * v11.4.0 - REFACTOR: Use LiftrModule snapshot helpers
 * v11.3.0 - NEW: Snapshot animations for progress bar and count pills
 * v11.2.3 - FIX: Selective exit transition for SLIDE mode buttons only
 * v11.2.0 - PERF: Optimized Loading→Success→Replace timing
 * v11.1.0 - NEW: Loading/Success/Error states for cart buttons
 * v11.0.0 - BREAKING: Selective Update replaces full innerHTML replacement
 * v10.8.0 - NEW: Discount code support
 * v10.7.0 - NEW: has-note and has-discounts state flags
 * v10.6.0 - NEW: Cart note support
 * v10.5.0 - REFACTOR: v4.3 Guidelines compliance
 */

import { LiftrModule } from './core.module.js';
import { on, i18n, settings, ATTRIBUTES, FX } from './core.liftr.js';
import { TYPES as NOTIFICATION_TYPES, DURATIONS } from './api.notification.js';
import './api.shop.js';

/* ════════════════════════════════════════════════════════════════════════════
 * MODULE CONSTANTS
 * ════════════════════════════════════════════════════════════════════════════ */

/**
 * Role constants for DOM queries.
 * @type {Readonly<Object.<string, string>>}
 */
const ROLES = Object.freeze({
  // Selective update wrappers
  CART_MAIN:       'cart-main',
  CART_DISCOUNT:   'cart-discount',
  CART_REWARDS:    'cart-rewards',
 
  // Existing roles
  LIVE:           'cart-live',
  NOTE:           'cart-note',
    
  ITEM:            'cart-item',
  ITEM_COUNT:      'cart-item-count',
  ITEM_PLUS:       'cart-item-plus',
  ITEM_MINUS:      'cart-item-minus',
  ITEM_REMOVE:     'cart-item-remove',
  ITEM_QUANTITY:   'cart-item-quantity',
  ITEM_PRICE:      'cart-item-price',
  
  // Subscription (per cart-item)
  SUBSCRIPTION_TOGGLE:      'cart-subscription-toggle',
  SUBSCRIPTION_PANEL:       'cart-subscription-panel',
  ITEM_SUBSCRIPTION_OPTION: 'cart-item-subscription-option',
  
  // Discount code elements
  DISCOUNT_COUNT:     'cart-discount-count',
  DISCOUNT_INPUT:     'cart-discount-input',
  DISCOUNT_APPLY:     'cart-discount-apply',
  DISCOUNT_TAG:       'cart-discount-tag',
  DISCOUNT_REMOVE:    'cart-discount-remove',
  DISCOUNT_ERROR:     'cart-discount-error',

  // Panel Orchestration (Nested Pattern)
  // Cart owns these panels and their toggles
  PANEL_MENU:         'cart-panel-menu',
  PANEL_NOTE:         'cart-panel-note',
  PANEL_DISCOUNT:     'cart-panel-discount',
  MENU_TOGGLE:        'cart-menu-toggle',
  NOTE_TOGGLE:        'cart-note-toggle',
  DISCOUNT_TOGGLE:    'cart-discount-toggle',
});

/**
 * State tokens for liftr-state attribute.
 * @type {Readonly<Object.<string, string>>}
 */
const STATES = Object.freeze({
  EMPTY:          'empty',
  LOADING:        'loading',
  SUCCESS:        'success',
  ERROR:          'error',
  ACTIVE:         'active',
  HAS_NOTE:       'has-note',
  HAS_DISCOUNTS:  'has-discounts'
});

/**
 * Event actions for this.emit().
 * @type {Readonly<Object.<string, string>>}
 */
const EVENTS = Object.freeze({
  REFRESHED:        'refreshed',
  ITEM_REMOVED:     'item-removed',
  PLAN_CHANGED:     'plan-changed',
  NOTE_SAVED:       'note-saved',
  DISCOUNT_APPLIED: 'discount-applied',
  DISCOUNT_REMOVED: 'discount-removed',
  DISCOUNT_ERROR:   'discount-error',
});

/**
 * External events this module listens to.
 * @type {Readonly<Object.<string, string>>}
 */
const LISTEN = Object.freeze({
  SHOP_READY:            'liftr:shop:ready',
  SHOP_CART_UPDATED:     'liftr:shop:cart-updated',
  SHOP_CART_CLEARED:     'liftr:shop:cart-cleared',
  SHOP_REWARD_UNLOCKED:  'liftr:shop:reward-unlocked',
  SHOP_REWARD_LOCKED:    'liftr:shop:reward-locked',
  SHOP_DISCOUNT_APPLIED: 'liftr:shop:discount-applied',
  SHOP_DISCOUNT_REMOVED: 'liftr:shop:discount-removed',
  PRODUCT_ADDING:        'liftr:product:adding',
  PRODUCT_ADDED:         'liftr:product:added',
  PRODUCT_ERROR:         'liftr:product:error',
  PANEL_CLOSED:          'liftr:panel:closed',
});

/**
 * Module-specific settings tokens.
 * @type {Readonly<Object.<string, string>>}
 */
const SETTINGS = Object.freeze({
  // No module-specific settings currently
});

/**
 * CSS custom properties set by JavaScript.
 * @type {Readonly<Object.<string, string>>}
 */
const PROPERTIES = Object.freeze({
  REWARDS_PROGRESS: '--rewards--progress'
});

const REFRESH_DEBOUNCE = 50;

/* ════════════════════════════════════════════════════════════════════════════
 * CART MODULE
 * ════════════════════════════════════════════════════════════════════════════ */

class LiftrCart extends LiftrModule {

  static NAME = 'cart';
  static DEFAULTS = {};

  #isUpdating = false;
  #refreshDebounceId = null;
  #pendingRefresh = false;
  #errorTimeoutId = null;
  
  // Deferred plan selection for sheet panels
  // Map<panelElement, { key, planId, item, originalPlanId }>
  #pendingSelections = new Map();

  // Deferred notifications (flushed after operation notification)
  #pendingNotifications = [];

  // Staggered flush timeout IDs (for cleanup on unmount)
  #flushTimeoutIds = [];

  // Product ATC coordination: defer reward flush until Full Cycle completes.
  // Product's try/catch guarantees added/error always fires — no timeout needed.
  #awaitingProductATC = false;

  // I18n labels (loaded once in mount)
  #labels = {};

  // Panel references (Nested Pattern)
  /** @type {LiftrPanel|null} */
  #panelMenu = null;
  /** @type {LiftrPanel|null} */
  #panelNote = null;
  /** @type {LiftrPanel|null} */
  #panelDiscount = null;

  /* ══════════════════════════════════════════════════════════════════════════
   * LIFECYCLE
   * ══════════════════════════════════════════════════════════════════════════ */

  mount() {
    // Load i18n labels once
    this.#labels = i18n.get('cart') || {};

    this.#bindClickDelegation();
    this.#bindExternalEvents();
    this.#bindPanelOrchestration();
    this.#bindPanelEvents();
    this.#bindDiscountEvents();
    
    if (window.liftrShop?.isInitialized()) {
      this.#mergeItems(this, false);
      this.#syncStates();
    } else {
      const offReady = on(LISTEN.SHOP_READY, () => {
        this.#mergeItems(this, false);
        this.#syncStates();
        offReady();
      });
      this.addDisposable(offReady);
    }
    
    this.#syncGlobalCounts();
  }

  unmount() {
    if (this.#refreshDebounceId) clearTimeout(this.#refreshDebounceId);
    if (this.#errorTimeoutId) clearTimeout(this.#errorTimeoutId);
    for (const id of this.#flushTimeoutIds) clearTimeout(id);
    this.#flushTimeoutIds = [];
    this.#awaitingProductATC = false;
    this.#pendingSelections.clear();
    this.#pendingNotifications = [];
    super.unmount();
  }

  /* ══════════════════════════════════════════════════════════════════════════
   * EVENT BINDING
   * ══════════════════════════════════════════════════════════════════════════ */

  #bindClickDelegation() {
    const handler = (e) => {
      const target = e.target;

      // Plus button
      if (target.closest(`[${ATTRIBUTES.ROLE}~="${ROLES.ITEM_PLUS}"]`)) {
        e.preventDefault();
        this.#handleQuantityChange(target, 1);
        return;
      }

      // Minus button
      if (target.closest(`[${ATTRIBUTES.ROLE}~="${ROLES.ITEM_MINUS}"]`)) {
        e.preventDefault();
        this.#handleQuantityChange(target, -1);
        return;
      }

      // Remove button
      if (target.closest(`[${ATTRIBUTES.ROLE}~="${ROLES.ITEM_REMOVE}"]`)) {
        e.preventDefault();
        this.#handleRemove(target);
        return;
      }

      // Subscription option (select plan) - behavior depends on panel type
      if (target.closest(`[${ATTRIBUTES.ROLE}~="${ROLES.ITEM_SUBSCRIPTION_OPTION}"]`)) {
        e.preventDefault();
        e.stopPropagation();
        this.#handlePlanSelect(target);
        return;
      }

      // Discount apply button
      if (target.closest(`[${ATTRIBUTES.ROLE}~="${ROLES.DISCOUNT_APPLY}"]`)) {
        e.preventDefault();
        this.#handleDiscountApply();
        return;
      }

      // Discount remove button (within tag)
      if (target.closest(`[${ATTRIBUTES.ROLE}~="${ROLES.DISCOUNT_REMOVE}"]`)) {
        e.preventDefault();
        this.#handleDiscountRemove(target);
        return;
      }

      // ═══════════════════════════════════════════════════════════════════
      // PANEL ORCHESTRATION (Nested Pattern via Click Delegation)
      // Survives Section Rendering DOM replacement
      // ═══════════════════════════════════════════════════════════════════

      // Menu toggle
      if (target.closest(`[${ATTRIBUTES.ROLE}~="${ROLES.MENU_TOGGLE}"]`)) {
        e.preventDefault();
        this.#panelMenu?.toggle(target);
        return;
      }

      // Note toggle (also closes menu)
      if (target.closest(`[${ATTRIBUTES.ROLE}~="${ROLES.NOTE_TOGGLE}"]`)) {
        e.preventDefault();
        this.#panelMenu?.close();
        this.#panelNote?.open(target);
        return;
      }

     // Discount toggle (also closes menu)
      if (target.closest(`[${ATTRIBUTES.ROLE}~="${ROLES.DISCOUNT_TOGGLE}"]`)) {
        e.preventDefault();
        this.#panelMenu?.close();
        this.#panelDiscount?.open(target);
        return;
      }

      // Subscription toggle (per cart-item, Nested Pattern)
      // Panel is inside cart-item, find it relative to clicked toggle
      if (target.closest(`[${ATTRIBUTES.ROLE}~="${ROLES.SUBSCRIPTION_TOGGLE}"]`)) {
        e.preventDefault();
        const toggle = target.closest(`[${ATTRIBUTES.ROLE}~="${ROLES.SUBSCRIPTION_TOGGLE}"]`);
        const item = toggle.closest(`[${ATTRIBUTES.ROLE}~="${ROLES.ITEM}"]`);
        const panel = item?.querySelector(`[${ATTRIBUTES.ROLE}~="${ROLES.SUBSCRIPTION_PANEL}"]`);
        panel?.toggle(toggle);
        return;
      }
    };

    this.addEventListener('click', handler);
    this.addDisposable(() => this.removeEventListener('click', handler));
  }

  #bindExternalEvents() {
    const offUpdated = on(LISTEN.SHOP_CART_UPDATED, (e) => {
      if (this.#isUpdating) return;
      
      // Note change: only sync states (no full refresh to keep panel open)
      if (e.detail?.action === 'note_changed') {
        this.#syncStates();
        return;
      }
      
      this.#scheduleRefresh();
    });
    this.addDisposable(offUpdated);

    // ─────────────────────────────────────────────────────────────────
    // PRODUCT ATC COORDINATION (v12.6.0)
    // Product emits adding → Cart defers flush → Product emits added/error → flush.
    // No timeout: Product's try/catch guarantees completion event.
    // ─────────────────────────────────────────────────────────────────

    const offProductAdding = on(LISTEN.PRODUCT_ADDING, () => {
      this.#awaitingProductATC = true;
    });
    this.addDisposable(offProductAdding);

    const offProductComplete = on(LISTEN.PRODUCT_ADDED, () => {
      this.#clearProductATCLock();
    });
    this.addDisposable(offProductComplete);

    const offProductError = on(LISTEN.PRODUCT_ERROR, () => {
      this.#clearProductATCLock();
    });
    this.addDisposable(offProductError);

    // ═══════════════════════════════════════════════════════════════════════
    // NESTED ATC GUARD (v12.3.0)
    // Products in recommendations slider live inside cart-main.
    // Their ATC buttons use Full Cycle (button stays in DOM).
    // Without guard: cart-added → refresh → DOM replace rips button mid-animation.
    // With guard: skip refresh here, let liftr:product:added trigger it after
    // the product's Full Cycle completes.
    // ═══════════════════════════════════════════════════════════════════════

    const offAdded = on(LISTEN.PRODUCT_ADDED, () => {
      if (this.#isUpdating) return;
      if (this.#hasActiveNestedATC()) return;
      this.#scheduleRefresh();
    });
    this.addDisposable(offAdded);

    // Deferred refresh: nested product completed its Full Cycle
    const offProductAdded = on(LISTEN.PRODUCT_ADDED, (e) => {
      if (this.#isUpdating) return;
      if (!this.contains(e.target)) return;
      this.#scheduleRefresh();
    });
    this.addDisposable(offProductAdded);

    const offCleared = on(LISTEN.SHOP_CART_CLEARED, () => {
      this.#scheduleRefresh();
    });
    this.addDisposable(offCleared);

    const offRewardUnlocked = on(LISTEN.SHOP_REWARD_UNLOCKED, (e) => {
      this.#scheduleRefresh();
      const name = e.detail?.name;
      if (name) {
        this.#pendingNotifications.push({
          type: NOTIFICATION_TYPES.INFO,   // ← war: NOTIFICATION_TYPES.SUCCESS
          title: this.#formatLabel('reward_unlocked', { name })
        });
      }
    });
    this.addDisposable(offRewardUnlocked);

    const offRewardLocked = on(LISTEN.SHOP_REWARD_LOCKED, (e) => {
      this.#scheduleRefresh();
      const { name, amountNeeded } = e.detail || {};
      if (name && amountNeeded) {
        const formatted = window.liftrShop?.pricing?.format(amountNeeded) || '';
        this.#pendingNotifications.push({
          type: NOTIFICATION_TYPES.INFO,
          title: this.#formatLabel('reward_locked', { amount: formatted, name })
        });
      }
    });
    this.addDisposable(offRewardLocked);

    // Discount events trigger refresh (tags need to update)
    const offDiscountApplied = on(LISTEN.SHOP_DISCOUNT_APPLIED, () => {
      if (this.#isUpdating) return;
      this.#scheduleRefresh();
    });
    this.addDisposable(offDiscountApplied);

    const offDiscountRemoved = on(LISTEN.SHOP_DISCOUNT_REMOVED, () => {
      if (this.#isUpdating) return;
      this.#scheduleRefresh();
    });
    this.addDisposable(offDiscountRemoved);
  }

  /**
   * Query nested panels (Nested Pattern).
   * Click delegation handles the actual events in #bindClickDelegation().
   */
  #bindPanelOrchestration() {
    // Query nested panels (Cart owns these via role)
    this.#panelMenu = this.role(ROLES.PANEL_MENU);
    this.#panelNote = this.role(ROLES.PANEL_NOTE);
    this.#panelDiscount = this.role(ROLES.PANEL_DISCOUNT);
  }

  /**
   * Bind panel close events for deferred plan selection.
   * Updated for Nested Pattern: uses panel element reference instead of liftr-id.
   */
  #bindPanelEvents() {
    const offClosed = on(LISTEN.PANEL_CLOSED, (e) => {
      const closedPanel = e.detail?.root;
      if (!closedPanel) return;

      // Note panel: save if changed
      if (closedPanel === this.#panelNote) {
        const textarea = closedPanel.querySelector(`[${ATTRIBUTES.ROLE}~="${ROLES.NOTE}"]`);
        const currentNote = textarea?.value ?? '';
        const savedNote = window.liftrShop?.cart?.get()?.note ?? '';
        if (currentNote !== savedNote) {
          this.#handleNoteSave(currentNote);
        }
        return;
      }

      // Subscription panel: deferred selection
      const pending = this.#pendingSelections.get(closedPanel);
      if (!pending) return;
      
      this.#pendingSelections.delete(closedPanel);
      
      if (pending.planId !== pending.originalPlanId) {
        this.#updateSellingPlan(pending.key, pending.planId, pending.item);
      }
    });
    this.addDisposable(offClosed);
  }

  /**
   * Bind discount input events (Enter key to apply).
   */
  #bindDiscountEvents() {
    const handler = (e) => {
      // Only handle Enter key in discount input
      if (e.key !== 'Enter') return;
      
      const input = e.target.closest(`[${ATTRIBUTES.ROLE}~="${ROLES.DISCOUNT_INPUT}"]`);
      if (!input) return;
      
      e.preventDefault();
      this.#handleDiscountApply();
    };

    this.addEventListener('keydown', handler);
    this.addDisposable(() => this.removeEventListener('keydown', handler));
  }

  /* ══════════════════════════════════════════════════════════════════════════
   * HANDLERS
   * ══════════════════════════════════════════════════════════════════════════ */

  async #handleQuantityChange(target, delta) {
    if (this.#isUpdating) return;

    const item = target.closest(`[${ATTRIBUTES.ROLE}~="${ROLES.ITEM}"]`);
    if (!item) return;

    const key = item.getAttribute(ATTRIBUTES.ID);
    if (!key) return;

    // Get clicked button for loading state
    const buttonRole = delta > 0 ? ROLES.ITEM_PLUS : ROLES.ITEM_MINUS;
    const button = target.closest(`[${ATTRIBUTES.ROLE}~="${buttonRole}"]`);

    const currentQty = this.#getItemQuantity(item);
    const newQty = Math.max(0, currentQty + delta);
    const mergeData = item.getAttribute(ATTRIBUTES.MERGE);

    // Track start time for MIN_LOADING
    const startTime = performance.now();

    if (newQty === 0) {
      await this.#removeItem(key, item, mergeData, button, startTime);
      return;
    }

    if (mergeData) {
      const { originalItems } = JSON.parse(mergeData);
      await this.#updateMergedItem(key, originalItems, newQty, item, button, startTime);
    } else {
      await this.#updateItem(key, newQty, item, button, startTime);
    }
  }

  async #handleRemove(target) {
    if (this.#isUpdating) return;

    const item = target.closest(`[${ATTRIBUTES.ROLE}~="${ROLES.ITEM}"]`);
    if (!item) return;

    const key = item.getAttribute(ATTRIBUTES.ID);
    if (!key) return;

    // Get clicked button for loading state
    const button = target.closest(`[${ATTRIBUTES.ROLE}~="${ROLES.ITEM_REMOVE}"]`);

    // Track start time for MIN_LOADING
    const startTime = performance.now();

    const mergeData = item.getAttribute(ATTRIBUTES.MERGE);
    await this.#removeItem(key, item, mergeData, button, startTime);
  }

  /**
   * Handle selling plan selection.
   * Behavior depends on panel type:
   * - Sheet: Mark selection visually, defer API call to panel close
   * - Dropdown: Close panel, then apply immediately
   */
  async #handlePlanSelect(target) {
    const option = target.closest(`[${ATTRIBUTES.ROLE}~="${ROLES.ITEM_SUBSCRIPTION_OPTION}"]`);
    if (!option) return;

    const planId = option.getAttribute(ATTRIBUTES.ID);
    
    const item = option.closest(`[${ATTRIBUTES.ROLE}~="${ROLES.ITEM}"]`);
    if (!item) return;

    const key = item.getAttribute(ATTRIBUTES.ID);
    if (!key) return;

    const numericPlanId = planId ? parseInt(planId, 10) : null;

    const panel = option.closest('liftr-panel');
    
    if (!panel) {
      await this.#updateSellingPlan(key, numericPlanId, item);
      return;
    }

    const panelMode = getComputedStyle(panel).getPropertyValue('--panel-mode').trim();
    const isSheet = panelMode === 'sheet';

    if (isSheet) {
      // Sheet: Mark selection visually, defer API call to panel close
      this.#markPendingSelection(option, panel, key, numericPlanId, item);
    } else {
      // Dropdown: Close and apply immediately
      panel.close();
      await this.#updateSellingPlan(key, numericPlanId, item);
    }
  }

  /**
   * Mark a plan selection as pending (for sheet panels).
   * Updates visual state without API call.
   * Uses panel element as Map key (Nested Pattern).
   */
  #markPendingSelection(option, panel, key, planId, item) {
    // Get original plan ID (from currently selected option)
    const currentSelected = panel.querySelector(
      `[${ATTRIBUTES.ROLE}~="${ROLES.ITEM_SUBSCRIPTION_OPTION}"][${ATTRIBUTES.STATE}~="${STATES.ACTIVE}"]`
    );
    const originalPlanId = currentSelected?.getAttribute(ATTRIBUTES.ID);
    const originalNumericPlanId = originalPlanId ? parseInt(originalPlanId, 10) : null;
    
    // Store pending selection using panel element as key
    const existing = this.#pendingSelections.get(panel);
    this.#pendingSelections.set(panel, {
      key,
      planId,
      item,
      originalPlanId: existing?.originalPlanId ?? originalNumericPlanId
    });
    
    // Update visual state: remove selected from all, add to selected option
    const allOptions = panel.querySelectorAll(
      `[${ATTRIBUTES.ROLE}~="${ROLES.ITEM_SUBSCRIPTION_OPTION}"]`
    );
    
    for (const opt of allOptions) {
      opt.removeAttribute(ATTRIBUTES.STATE);
      opt.removeAttribute('aria-selected');
    }
    
    option.setAttribute(ATTRIBUTES.STATE, STATES.ACTIVE);
    option.setAttribute('aria-selected', 'true');
  }

  /**
   * Save cart note via API.
   * State sync happens via liftr:shop:cart-updated event.
   * @param {string} note - Note content
   */
  async #handleNoteSave(note) {
    if (this.#isUpdating) return;

    try {
      await window.liftrShop?.cart?.setNote(note);
      this.emit(EVENTS.NOTE_SAVED, { note });
      
      const message = this.#labels.note_saved;
      this.#announce(message);
      this.#notify(NOTIFICATION_TYPES.SUCCESS, message, { duration: DURATIONS.SHORT });

    } catch {
      // Silently handle note save errors
    }
  }

  /**
   * Apply discount code from input field.
   * Uses createState for proper fx-loading lifecycle management.
   */
  async #handleDiscountApply() {
    if (this.#isUpdating) return;

    const input = this.role(ROLES.DISCOUNT_INPUT);
    if (!input) return;

    const code = input.value.trim().toUpperCase();
    if (!code) {
      this.#showDiscountError(this.#labels.discount_empty);
      return;
    }

    const button = this.role(ROLES.DISCOUNT_APPLY);
    const btnState = button ? this.createState(button) : null;

    this.#setUpdating(true);
    this.#clearDiscountError();
    btnState?.add(STATES.LOADING);

    // Track start time for MIN_LOADING
    const startTime = performance.now();

    try {
      await window.liftrShop?.cart?.applyDiscount(code);

      const isApplicable = window.liftrShop?.cart?.isDiscountApplicable(code);

      if (!isApplicable) {
        await window.liftrShop?.cart?.removeDiscount(code);
        this.#showDiscountError(this.#labels.discount_not_applicable);
        this.emit(EVENTS.DISCOUNT_ERROR, { code, reason: 'not_applicable' });
        this.#notify(NOTIFICATION_TYPES.ERROR, this.#formatLabel('discount_not_applicable', { code }));
        this.#notifyDiscountHint(code);
        
        // Error state with full lifecycle
        
        // Error state with full lifecycle
        btnState?.remove(STATES.LOADING);
        btnState?.add(STATES.ERROR);
        await this.awaitResult(button, true);
        await this.awaitResultExit(button, btnState, STATES.ERROR);
        return;
      }

      // Success - enforce MIN_LOADING, show SUCCESS, then refresh
      await this.awaitLoading(startTime);
      
      input.value = '';
      
      const appliedMessage = this.#formatLabel('discount_applied', { code });
      
      btnState?.remove(STATES.LOADING);
      btnState?.add(STATES.SUCCESS);
      await this.awaitResult(button);
      await this.#refresh();
      
      this.emit(EVENTS.DISCOUNT_APPLIED, { code });
      this.#announce(appliedMessage);
      this.#notify(NOTIFICATION_TYPES.SUCCESS, appliedMessage);

    } catch (error) {
      this.#showDiscountError(this.#labels.discount_error);
      this.emit(EVENTS.DISCOUNT_ERROR, { code, reason: 'error', error });
      this.#notify(NOTIFICATION_TYPES.ERROR, this.#labels.discount_error);
      
      // Error state with full lifecycle
      btnState?.remove(STATES.LOADING);
      btnState?.add(STATES.ERROR);
      await this.awaitResult(button, true);
      await this.awaitResultExit(button, btnState, STATES.ERROR);
    } finally {
      this.#setUpdating(false);
    }
  }

  /**
   * Remove a single discount code.
   * Uses createState for proper fx-loading lifecycle management.
   * @param {HTMLElement} target - The remove button clicked
   */
  async #handleDiscountRemove(target) {
    if (this.#isUpdating) return;

    const tag = target.closest(`[${ATTRIBUTES.ROLE}~="${ROLES.DISCOUNT_TAG}"]`);
    if (!tag) return;

    const code = tag.getAttribute(ATTRIBUTES.ID)?.toUpperCase();
    if (!code) return;

    const button = target.closest(`[${ATTRIBUTES.ROLE}~="${ROLES.DISCOUNT_REMOVE}"]`);
    const btnState = button ? this.createState(button) : null;
    btnState?.add(STATES.LOADING);

    this.#setUpdating(true);

    // Track start time for MIN_LOADING
    const startTime = performance.now();

    try {
      await window.liftrShop?.cart?.removeDiscount(code);
      
      // Enforce MIN_LOADING, show SUCCESS, then refresh
      await this.awaitLoading(startTime);
      
      const removedMessage = this.#formatLabel('discount_removed', { code });
      
      btnState?.remove(STATES.LOADING);
      btnState?.add(STATES.SUCCESS);
      await this.awaitResult(button);
      await this.#refresh();
      
      this.emit(EVENTS.DISCOUNT_REMOVED, { code });
      this.#announce(removedMessage);
      this.#notify(NOTIFICATION_TYPES.SUCCESS, removedMessage);
    } catch {
      this.#notify(NOTIFICATION_TYPES.ERROR, this.#labels.discount_error);
      
      // Error state with full lifecycle
      btnState?.remove(STATES.LOADING);
      btnState?.add(STATES.ERROR);
      await this.awaitResult(button, true);
      await this.awaitResultExit(button, btnState, STATES.ERROR);
    } finally {
      this.#setUpdating(false);
    }
  }

  /**
   * Show discount error message.
   * @param {string} message - Error message to display
   */
  #showDiscountError(message) {
    const errorEl = this.role(ROLES.DISCOUNT_ERROR);
    if (!errorEl) return;

    errorEl.textContent = message;
    errorEl.hidden = false;

    // Auto-hide after duration
    if (this.#errorTimeoutId) {
      clearTimeout(this.#errorTimeoutId);
    }
    this.#errorTimeoutId = setTimeout(() => {
      this.#clearDiscountError();
    }, DURATIONS.LONG);
  }

  /**
   * Clear discount error message.
   */
  #clearDiscountError() {
    const errorEl = this.role(ROLES.DISCOUNT_ERROR);
    if (!errorEl) return;

    errorEl.textContent = '';
    errorEl.hidden = true;

    if (this.#errorTimeoutId) {
      clearTimeout(this.#errorTimeoutId);
      this.#errorTimeoutId = null;
    }
  }

  /* ══════════════════════════════════════════════════════════════════════════
   * CART OPERATIONS
   * ══════════════════════════════════════════════════════════════════════════ */

  /**
   * Generic cart operation with loading/success/error states.
   * Single source of truth for the LOADING → SUCCESS → Refresh flow.
   * Uses createState() for proper StateManager lifecycle and
   * base class helpers: awaitLoading(), awaitResult(), awaitResultExit().
   * 
   * Optional validate callback runs after API success but before
   * showing SUCCESS state. Catches silent failures like Shopify
   * capping quantity to inventory limit (200 OK, unchanged data).
   * 
   * @param {Object} options
   * @param {HTMLElement} options.item - Cart item element
   * @param {HTMLElement|null} options.button - Trigger button for fx-loading state
   * @param {Function} options.operation - Async API call to perform
   * @param {string} options.message - ARIA announcement on success
   * @param {string} [options.notificationMessage] - Visual notification message (defaults to message)
   * @param {Function} [options.onSuccess] - Optional callback after refresh (for events)
   * @param {number} [options.startTime] - Operation start time for MIN_LOADING
   * @param {Function} [options.validate] - Post-operation validation. Returns { ok, message }
   */
  async #performCartOperation({ item, button, operation, message, notificationMessage, onSuccess, startTime = performance.now(), validate }) {
    this.#setUpdating(true);
    this.#setItemState(item, STATES.LOADING);
    
    const btnState = button ? this.createState(button) : null;
    btnState?.add(STATES.LOADING);

    try {
      await operation();
      await this.awaitLoading(startTime);

      // ═══════════════════════════════════════════════════════════════════
      // POST-CHECK: Validate actual result vs expected (v12.4.0)
      // Catches silent Shopify caps (e.g. quantity → inventory limit).
      // API returns 200 OK but data unchanged → misleading success.
      // On failure: skip SUCCESS state, refresh to sync DOM, notify user.
      // ═══════════════════════════════════════════════════════════════════
      if (validate) {
        const result = validate();
        if (!result.ok) {
          btnState?.remove(STATES.LOADING);
          await this.#refresh();
          this.#announce(result.message);
          this.#notify(NOTIFICATION_TYPES.INFO, result.message);
          return;
        }
      }
      
      btnState?.remove(STATES.LOADING);
      btnState?.add(STATES.SUCCESS);
      await this.awaitResult(button);
      await this.#refresh();
      
      onSuccess?.();
      this.#announce(message);
      
      // Show visual notification (use notificationMessage or fallback to message)
      this.#notify(NOTIFICATION_TYPES.SUCCESS, notificationMessage || message);

      // Flush deferred reward notifications (after operation notification)
      this.#flushNotifications();
    } catch {
      btnState?.remove(STATES.LOADING);
      btnState?.add(STATES.ERROR);
      await this.awaitResult(button, true);
      await this.awaitResultExit(button, btnState, STATES.ERROR);
      
      this.#setItemState(item, null);
      
      // Show error notification
      this.#notify(NOTIFICATION_TYPES.ERROR, this.#labels.error_generic);
    } finally {
      this.#setUpdating(false);
    }
  }

  /**
   * Update item quantity.
   * Validates actual quantity after API call to catch silent inventory caps.
   */
  async #updateItem(key, quantity, item, button = null, startTime = performance.now()) {
    await this.#performCartOperation({
      item,
      button,
      startTime,
      operation: () => window.liftrShop?.cart?.update(key, quantity),
      message: this.#labels.item_updated,
      validate: () => this.#validateQuantity(key, quantity),
    });
  }

  /**
   * Update merged item quantity.
   * Validates actual quantity after API call to catch silent inventory caps.
   * @param {string} key - Cart item key (for post-check validation)
   */
  async #updateMergedItem(key, originalItems, quantity, item, button = null, startTime = performance.now()) {
    await this.#performCartOperation({
      item,
      button,
      startTime,
      operation: () => window.liftrShop?.cart?.updateMerged(originalItems, quantity),
      message: this.#labels.item_updated,
      validate: () => this.#validateQuantity(key, quantity),
    });
  }

  /**
   * Validate that cart item quantity matches requested quantity.
   * Catches silent Shopify inventory caps (200 OK, quantity unchanged).
   * 
   * After cart.update() or cart.updateMerged(), the internal cart state
   * reflects Shopify's response. If Shopify capped the quantity to
   * inventory limit, the actual quantity will be lower than requested.
   * 
   * @param {string} key - Cart item key
   * @param {number} requestedQty - Quantity that was requested
   * @returns {{ ok: boolean, message?: string }}
   */
  #validateQuantity(key, requestedQty) {
    const cart = window.liftrShop?.cart?.get();
    if (!cart) return { ok: true };

    const cartItem = cart.items?.find(i => i.key === key || i._keys?.includes(key));
    if (!cartItem) return { ok: true };

    if (cartItem.quantity < requestedQty) {
      return { ok: false, message: this.#labels.stock_limit };
    }

    return { ok: true };
  }

  /**
   * Remove item from cart.
   */
  async #removeItem(key, item, mergeData = null, button = null, startTime = performance.now()) {
    const operation = mergeData
      ? () => {
          const { originalItems } = JSON.parse(mergeData);
          return window.liftrShop?.cart?.updateMerged(originalItems, 0);
        }
      : () => window.liftrShop?.cart?.remove(key);

    await this.#performCartOperation({
      item,
      button,
      startTime,
      operation,
      message: this.#labels.item_removed,
      onSuccess: () => this.emit(EVENTS.ITEM_REMOVED, { key })
    });
  }

  /**
   * Update selling plan for item.
   */
  async #updateSellingPlan(key, planId, item) {
    const trigger = item.querySelector(`[${ATTRIBUTES.ROLE}~="${ROLES.SUBSCRIPTION_TOGGLE}"]`);
    
    const message = planId 
      ? this.#labels.subscription_activated
      : this.#labels.subscription_removed;
    
    await this.#performCartOperation({
      item,
      button: trigger,
      operation: () => window.liftrShop?.cart?.setSellingPlan(key, planId),
      message,
      onSuccess: () => this.emit(EVENTS.PLAN_CHANGED, { key, planId })
    });
  }

  /* ══════════════════════════════════════════════════════════════════════════
   * SECTION RENDERING (SELECTIVE UPDATE)
   * ══════════════════════════════════════════════════════════════════════════ */

  #scheduleRefresh() {
    if (this.#pendingRefresh) return;
    
    this.#pendingRefresh = true;
    
    if (this.#refreshDebounceId) {
      clearTimeout(this.#refreshDebounceId);
    }
    
    this.#refreshDebounceId = setTimeout(async () => {
      this.#pendingRefresh = false;
      
      if (this.#isUpdating) return;
      
      try {
        await this.#refresh();
      } catch {
        // Silently handle refresh errors
      }
    }, REFRESH_DEBOUNCE);
  }

  /**
   * Refresh cart via Section Rendering API.
   * Uses selective update: only replace wrapper content, panels stay open.
   * 
   * Snapshot Pattern:
   * - Capture values BEFORE DOM replace
   * - Apply animations to NEW elements AFTER replace via base class helpers
   * 
   * Note: Button state cleanup (awaitResultExit) happens in callers
   * BEFORE #refresh() is called. The SLIDE exit block below is a
   * safety net for edge cases (e.g. external refresh during state).
   */
  async #refresh() {
    const section = this.closest('.shopify-section');
    if (!section) {
      throw new Error('Cart must be inside .shopify-section');
    }

    // ═══════════════════════════════════════════════════════════════════
    // SNAPSHOT: Capture values BEFORE replace
    // ═══════════════════════════════════════════════════════════════════
    const oldRewardsValue = this.role(ROLES.CART_REWARDS)
      ?.style.getPropertyValue(PROPERTIES.REWARDS_PROGRESS)?.trim() || null;
    const oldItemCount = this.role(ROLES.ITEM_COUNT)?.textContent?.trim();
    const oldDiscountCount = this.role(ROLES.DISCOUNT_COUNT)?.textContent?.trim();

    // ═══════════════════════════════════════════════════════════════════
    // SAFETY NET: Clean up any lingering button states before DOM replace.
    // Normally callers handle exit via awaitResultExit() before calling
    // #refresh(), but external refreshes may catch buttons mid-state.
    // ═══════════════════════════════════════════════════════════════════
    const slideButtonsWithState = this.querySelectorAll(
      `.${FX.LOADING}:has(.button-label)[${ATTRIBUTES.STATE}]`
    );
    
    if (slideButtonsWithState.length) {
      for (const btn of slideButtonsWithState) {
        btn.removeAttribute(ATTRIBUTES.STATE);
      }
      await new Promise(r => setTimeout(r, this.getExitDuration()));
    }

    const sectionId = section.id.replace('shopify-section-', '');

    // Fetch fresh section HTML
    const url = `${location.pathname}?section_id=${sectionId}`;
    const response = await fetch(url);

    if (!response.ok) {
      throw new Error(`Section fetch failed: ${response.status}`);
    }

    const html = await response.text();

    // Parse template
    const template = document.createElement('template');
    template.innerHTML = html;
    const freshCart = template.content.querySelector('liftr-cart');

    if (!freshCart) {
      throw new Error('No liftr-cart found in section response');
    }

    // Pre-DOM merging
    this.#mergeItems(freshCart, true);

    // ═══════════════════════════════════════════════════════════════════
    // DOM REPLACE
    // ═══════════════════════════════════════════════════════════════════
    this.#replaceByRole(ROLES.CART_MAIN, freshCart);
    this.#replaceByRole(ROLES.CART_DISCOUNT, freshCart);

    // ═══════════════════════════════════════════════════════════════════
    // RE-QUERY PANELS after DOM replace (they might be inside replaced content)
    // ═══════════════════════════════════════════════════════════════════
    this.#panelMenu = this.role(ROLES.PANEL_MENU);
    // Note: panelNote and panelDiscount are outside cart-main, no re-query needed

    // ═══════════════════════════════════════════════════════════════════
    // ANIMATE: Using base class snapshot helpers
    // ═══════════════════════════════════════════════════════════════════
    this.morphProperty(ROLES.CART_REWARDS, PROPERTIES.REWARDS_PROGRESS, oldRewardsValue);
    this.animateOnChange(ROLES.ITEM_COUNT, oldItemCount);
    this.animateOnChange(ROLES.DISCOUNT_COUNT, oldDiscountCount);

    // Sync states
    this.#syncStates();
    this.#syncGlobalCounts();

    this.emit(EVENTS.REFRESHED);

    // Flush deferred reward notifications (external triggers only —
    // #performCartOperation handles its own flush with proper sequencing)
     if (!this.#isUpdating && !this.#awaitingProductATC) {
      this.#flushNotifications();
    }
  }

  /**
   * Replace element by role with fresh content.
   * Uses display: contents wrapper pattern for seamless update.
   * @param {string} role - Role name to find and replace
   * @param {HTMLElement} freshCart - Fresh cart element from section fetch
   */
  #replaceByRole(role, freshCart) {
    const oldEl = this.querySelector(`[${ATTRIBUTES.ROLE}="${role}"]`);
    const newEl = freshCart.querySelector(`[${ATTRIBUTES.ROLE}="${role}"]`);
    
    if (oldEl && newEl) {
      oldEl.replaceWith(newEl.cloneNode(true));
    }
  }

  /* ══════════════════════════════════════════════════════════════════════════
   * ITEM MERGING
   * ══════════════════════════════════════════════════════════════════════════ */

  #mergeItems(container, removeNodes = false) {
    const cart = window.liftrShop?.cart?.get();
    if (!cart) return;

    const keyToMerged = new Map();
    for (const item of cart.items) {
      for (const key of item._keys) {
        keyToMerged.set(key, item);
      }
    }

    const processedGroups = new Set();
    const items = container.querySelectorAll(`[${ATTRIBUTES.ROLE}~="${ROLES.ITEM}"]`);

    for (const domItem of items) {
      const key = domItem.getAttribute(ATTRIBUTES.ID);
      if (!key) continue;

      const mergedItem = keyToMerged.get(key);
      if (!mergedItem) continue;

      const groupId = mergedItem._keys[0];

      if (mergedItem._isMerged) {
        if (processedGroups.has(groupId)) {
          if (removeNodes) {
            domItem.remove();
          } else {
            domItem.hidden = true;
            domItem.setAttribute('aria-hidden', 'true');
          }
        } else {
          processedGroups.add(groupId);
          
          domItem.hidden = false;
          domItem.removeAttribute('aria-hidden');

          this.#updateQuantityDisplay(domItem, mergedItem.quantity);
          this.#updatePriceDisplay(domItem, mergedItem.formatted.linePrice);

          domItem.setAttribute(ATTRIBUTES.MERGE, JSON.stringify({
            keys: mergedItem._keys,
            originalItems: mergedItem._originalItems
          }));
        }
      } else {
        domItem.hidden = false;
        domItem.removeAttribute('aria-hidden');
        domItem.removeAttribute(ATTRIBUTES.MERGE);
      }
    }
  }

  #updateQuantityDisplay(item, quantity) {
    // Scoped query: find quantity element within item
    const qtyEl = item.querySelector(`[${ATTRIBUTES.ROLE}~="${ROLES.ITEM_QUANTITY}"]`);
    if (!qtyEl) return;

    if (qtyEl.tagName === 'INPUT') {
      qtyEl.value = quantity;
    } else {
      qtyEl.textContent = quantity;
    }
  }

  #updatePriceDisplay(item, formattedPrice) {
    // Scoped query: find price element within item
    const priceEl = item.querySelector(`[${ATTRIBUTES.ROLE}~="${ROLES.ITEM_PRICE}"]`);
    if (!priceEl) return;

    priceEl.textContent = formattedPrice;
  }

  /* ══════════════════════════════════════════════════════════════════════════
   * NESTED ATC GUARD
   * ══════════════════════════════════════════════════════════════════════════ */

  /**
   * Check if a nested liftr-product inside cart-main has an active ATC flow.
   * Prevents DOM replace (Section Rendering) while fx-loading button is
   * mid-cycle (LOADING or SUCCESS state).
   * 
   * Context: Product cards in the recommendations slider live inside
   * cart-main. Their variant panel ATC uses Full Cycle (button stays
   * in DOM). Without this guard, liftr:product:added triggers a
   * refresh that replaces cart-main and rips the button mid-animation.
   * 
   * @returns {boolean} True if a nested product ATC is in progress
   */
  #hasActiveNestedATC() {
    const main = this.querySelector(`[${ATTRIBUTES.ROLE}="${ROLES.CART_MAIN}"]`);
    if (!main) return false;

    return !!main.querySelector(
      `.fx-loading[${ATTRIBUTES.STATE}~="${STATES.LOADING}"], .fx-loading[${ATTRIBUTES.STATE}~="${STATES.SUCCESS}"]`
    );
  }

/* ══════════════════════════════════════════════════════════════════════════
   * DISCOUNT MBW HINT
   * ══════════════════════════════════════════════════════════════════════════ */

  /**
   * Show MBW hint notification for inapplicable discount code.
   * Looks up code in settings.discounts (exact match, then longest prefix).
   * Only shows when cart total is below the discount's minimum order value.
   * 
   * @param {string} code - Discount code (already uppercase)
   */
  #notifyDiscountHint(code) {
    const discounts = settings.discounts;
    if (!discounts || !Object.keys(discounts).length) return;

    // Exact match
    let entry = discounts[code];

    // Prefix match (longest first)
    if (!entry) {
      const prefixes = Object.keys(discounts).sort((a, b) => b.length - a.length);
      const match = prefixes.find(p => code.startsWith(p));
      if (match) entry = discounts[match];
    }

    if (!entry?.threshold) return;

    const cart = window.liftrShop?.cart?.get();
    if (!cart) return;

    const cartTotal = cart.total_price;
    const thresholdCents = entry.threshold;

    if (cartTotal >= thresholdCents) return;

    const formatted = window.liftrShop?.pricing?.format(thresholdCents) || entry.threshold.amount;

    // Choose label: with or without expiry
    const labelKey = entry.ends_at ? 'discount_hint_expires' : 'discount_hint';
    const vars = { threshold: formatted };

    if (entry.ends_at) {
      const date = new Date(entry.ends_at);
      vars.date = date.toLocaleDateString(i18n.locale, { day: 'numeric', month: 'short' });
    }

    // ═══════════════════════════════════════════════════════════════════
    // STAGGER: Push to queue, don't fire directly (v12.5.0 pattern)
    // Direct #notify() here would overlap with the error notification.
    // ═══════════════════════════════════════════════════════════════════
    this.#pendingNotifications.push({
      type: NOTIFICATION_TYPES.INFO,
      title: this.#formatLabel(labelKey, vars)
    });
    this.#flushNotifications();
  }

  /* ══════════════════════════════════════════════════════════════════════════
   * NOTIFICATION HELPERS
   * ══════════════════════════════════════════════════════════════════════════ */

  /**
   * Show visual notification via Notification API.
   * Gracefully degrades if liftrNotification is not available.
   * 
   * @param {string} type - Notification type (success, error, info, warning)
   * @param {string} title - Notification title
   * @param {string|null} [message] - Optional message body
   * @param {Object} [options] - Additional options (duration, priority)
   */
  #notify(type, title, options = {}) {
    if (!window.liftrNotification) return;
    
    const method = window.liftrNotification[type];
    if (typeof method !== 'function') return;
    
    method.call(window.liftrNotification, title, {
      duration: options.duration ?? DURATIONS.NORMAL,
      ...options
    });
  }

  /**
   * Flush queued notifications (deferred from reward events).
   * Called after operation notification in #performCartOperation()
   * so reward notifications appear sequentially after the action feedback.
   * Each notification is offset by (index + 1) × NOTIFICATION_STAGGER
   * to prevent simultaneous dispatch.
   */
  #flushNotifications() {
    if (!this.#pendingNotifications.length) return;

    const pending = [...this.#pendingNotifications];
    this.#pendingNotifications = [];

    const stagger = this.getCssDuration(FX.PROPERTIES.NOTIFICATION_STAGGER, 200);

    for (let i = 0; i < pending.length; i++) {
      const { type, title } = pending[i];
      this.#flushTimeoutIds.push(
        setTimeout(() => this.#notify(type, title), stagger * (i + 1))
      );
    }
  }

  /**
   * Clear product ATC coordination lock and flush pending notifications.
   * Called when liftr:product:added or liftr:product:error arrives.
   */
  #clearProductATCLock() {
    if (!this.#awaitingProductATC) return;
    this.#awaitingProductATC = false;
    this.#flushNotifications();
  }

  /* ══════════════════════════════════════════════════════════════════════════
   * HELPERS
   * ══════════════════════════════════════════════════════════════════════════ */

  /**
   * Format label with variable substitution.
   * @param {string} key - Label key
   * @param {Object} vars - Variables to substitute
   * @returns {string}
   */
  #formatLabel(key, vars = {}) {
    let text = this.#labels[key] || key;
    
    for (const [k, v] of Object.entries(vars)) {
      text = text.replace(`{{${k}}}`, v);
    }
    
    return text;
  }

  #getItemQuantity(item) {
    // Scoped query: find quantity element within item
    const qtyEl = item.querySelector(`[${ATTRIBUTES.ROLE}~="${ROLES.ITEM_QUANTITY}"]`);
    if (!qtyEl) return 1;

    const value = qtyEl.value ?? qtyEl.textContent;
    return parseInt(value, 10) || 1;
  }

  #setItemState(item, state) {
    if (state) {
      item.setAttribute(ATTRIBUTES.STATE, state);
    } else {
      item.removeAttribute(ATTRIBUTES.STATE);
    }
  }

  #setUpdating(updating) {
    this.#isUpdating = updating;
    this.state.toggle(STATES.LOADING, updating);
  }

  /**
   * Sync all cart states from API data (single source of truth).
   * - empty: No visible items
   * - has-note: Cart has a note
   * - has-discounts: Cart has discounts applied
   */
  #syncStates() {
    const cart = window.liftrShop?.cart?.get();
    
    // Empty state (from DOM - visible items)
    const items = this.roleAll(ROLES.ITEM);
    const hasVisible = Array.prototype.some.call(items, item => !item.hidden);
    this.state.toggle(STATES.EMPTY, !hasVisible);
    
    // has-note state (from API)
    const hasNote = !!cart?.note?.trim();
    this.state.toggle(STATES.HAS_NOTE, hasNote);
    
    // has-discounts state (from API)
    const hasDiscounts = cart?.discounts?.hasDiscounts ?? false;
    this.state.toggle(STATES.HAS_DISCOUNTS, hasDiscounts);
  }

  #syncGlobalCounts() {
    const countEl = this.role(ROLES.ITEM_COUNT);
    if (!countEl) return;

    const newCount = countEl.textContent?.trim() || '0';

    // Sync to elements outside this module (header cart count etc.)
    document.querySelectorAll(`[${ATTRIBUTES.ROLE}~="${ROLES.ITEM_COUNT}"]`).forEach(el => {
      if (el === countEl) return;
      el.textContent = newCount;
    });
  }

  #announce(message) {
    const live = this.role(ROLES.LIVE);
    if (!live) return;

    live.textContent = message;

    setTimeout(() => {
      live.textContent = '';
    }, 1000);
  }
}

/* ════════════════════════════════════════════════════════════════════════════
 * REGISTER WEB COMPONENT
 * ════════════════════════════════════════════════════════════════════════════ */

customElements.define('liftr-cart', LiftrCart);

export { LiftrCart, ROLES, STATES, EVENTS, LISTEN, SETTINGS, PROPERTIES };